import { Autocomplete, Box, Chip, Stack, TextField, Typography, useTheme } from '@mui/material';
import { TagResponse } from 'src/types/tags/tags';
import { TagMapping, TemplateTag } from '../../types';
import type { UsageCategory } from 'src/features/sites/hooks';

// Narrower source-tag column, fixed-width relates-to column, larger tag-name column
const GRID = 'minmax(0, 0.8fr) minmax(0, 0.9fr) minmax(0, 1.3fr)';

// Shortens the label on mobile to avoid it covering the dropwdown arrow
const TAG_NAME_FIELD_SX = {
  '& .MuiInputLabel-root:not(.MuiInputLabel-shrink)': { maxWidth: 'calc(100% - 56px)' },
};

// Mobile horizontal scroll for tag dropwdown selection
const MOBILE_LISTBOX_SX = {
  overflowX: 'auto',
  '& .MuiAutocomplete-option': { width: 'max-content', minWidth: '100%', whiteSpace: 'nowrap' },
};

export type RegularTagRowProps = {
  rawInputName: string;
  tag: TemplateTag;
  categories: UsageCategory[];
  destinationTags: TagResponse[];
  tagMapping: TagMapping;
  onTagMappingChange: (sourceTagId: string, destinationTagId: string) => void;
  theme: ReturnType<typeof useTheme>;
};

export const RegularTagRow = ({
  rawInputName,
  tag,
  categories,
  destinationTags,
  tagMapping,
  onTagMappingChange,
  theme,
}: RegularTagRowProps) => (
  <Box
    sx={{
      display: 'grid',
      gridTemplateColumns: GRID,
      gap: 1,
      alignItems: 'start',
      px: 1,
      py: 0.75,
      borderBottom: `1px solid ${theme.palette.divider}`,
    }}
  >
    {/* Template tag name */}
    <Stack spacing={0.25} sx={{ minWidth: 0, pt: 0.25 }}>
      <Typography variant="body2" noWrap title={tag.displayName ?? rawInputName}>
        {tag.displayName || rawInputName}
      </Typography>
      <Typography variant="caption" color="text.secondary" noWrap>
        {rawInputName}
        {tag.dataType ? ` · ${tag.dataType}` : ''}
        {tag.rawUnit ? ` · ${tag.rawUnit}` : ''}
      </Typography>
    </Stack>

    {/* Relates to pills — wrap within column, never overflow */}
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, minWidth: 0, pt: 0.25 }}>
      {categories.map((cat) => (
        <Chip
          key={cat}
          label={cat}
          size="small"
          variant="outlined"
          color="primary"
          sx={{ fontSize: '0.65rem', height: 20, fontWeight: 500 }}
        />
      ))}
    </Box>

    {/* Destination tag selector with search */}
    <Autocomplete
      size="small"
      fullWidth
      options={destinationTags}
      getOptionLabel={(opt) => opt.displayName || opt.rawInputName}
      ListboxProps={{ sx: { [theme.breakpoints.down('sm')]: MOBILE_LISTBOX_SX } }}
      value={destinationTags.find((t) => t.id === tagMapping[tag.idempotencyKey]) ?? null}
      onChange={(_, selected) => onTagMappingChange(tag.idempotencyKey, selected?.id ?? '')}
      renderInput={(params) => <TextField {...params} label="Tag name" sx={TAG_NAME_FIELD_SX} />}
      isOptionEqualToValue={(opt, val) => opt.id === val.id}
      clearOnEscape
    />
  </Box>
);
